import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Users, Home, CheckCircle } from 'lucide-react';
import { familyService, FamilyGroup, FamilyMember } from '@/services/familyService';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

interface Guest {
  name?: string;
  email?: string;
  phone?: string;
}

interface FamilyMemberSelectorProps {
  selectedGuests: Guest[];
  onGuestsChange: (guests: Guest[]) => void;
  familyGroupId?: string;
}

export const FamilyMemberSelector: React.FC<FamilyMemberSelectorProps> = ({
  selectedGuests,
  onGuestsChange,
  familyGroupId: propFamilyGroupId
}) => {
  const [familyGroups, setFamilyGroups] = useState<FamilyGroup[]>([]);
  const [selectedFamilyGroupId, setSelectedFamilyGroupId] = useState<string>(propFamilyGroupId || '');
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadFamilyGroups();
  }, []);

  useEffect(() => {
    if (selectedFamilyGroupId) {
      loadFamilyMembers(selectedFamilyGroupId);
    }
  }, [selectedFamilyGroupId]);

  useEffect(() => {
    if (propFamilyGroupId) {
      setSelectedFamilyGroupId(propFamilyGroupId);
    }
  }, [propFamilyGroupId]);

  const loadFamilyGroups = async () => {
    try {
      const groups = await familyService.getUserFamilyGroups();
      setFamilyGroups(groups);
      
      // Auto-select if only one group
      if (groups.length === 1 && !propFamilyGroupId) {
        setSelectedFamilyGroupId(groups[0].id);
      }
    } catch (error) {
      console.error('Error loading family groups:', error);
      toast.error('Failed to load family groups');
    }
  };

  const loadFamilyMembers = async (familyGroupId: string) => {
    try {
      setLoading(true);
      const members = await familyService.getFamilyMembers(familyGroupId);
      setFamilyMembers(members.filter(m => m.status === 'active' && (m.email || m.phone)));
    } catch (error) {
      console.error('Error loading family members:', error);
      toast.error('Failed to load family members');
    } finally {
      setLoading(false);
    }
  };

  const isMemberSelected = (member: FamilyMember): boolean => {
    return selectedGuests.some(guest => 
      (guest.email && guest.email === member.email) ||
      (guest.phone && guest.phone === member.phone)
    );
  };

  const handleMemberToggle = (member: FamilyMember) => {
    const isSelected = isMemberSelected(member);
    
    if (isSelected) {
      // Remove member
      const updatedGuests = selectedGuests.filter(guest => 
        !(guest.email === member.email || guest.phone === member.phone)
      );
      onGuestsChange(updatedGuests);
    } else {
      // Add member
      const newGuest: Guest = {
        name: member.name,
        email: member.email,
        phone: member.phone,
      };
      onGuestsChange([...selectedGuests, newGuest]);
    }
  };

  const selectAllMembers = () => {
    const allMemberGuests = familyMembers.map(member => ({
      name: member.name,
      email: member.email,
      phone: member.phone,
    }));
    
    // Merge with existing guests, avoiding duplicates
    const existingGuestIdentifiers = new Set(
      selectedGuests.map(g => g.email || g.phone).filter(Boolean)
    );
    
    const newGuests = allMemberGuests.filter(guest => 
      !existingGuestIdentifiers.has(guest.email || guest.phone || '')
    );
    
    onGuestsChange([...selectedGuests, ...newGuests]);
  };

  const clearAllMembers = () => {
    // Remove only family members from selected guests
    const memberIdentifiers = new Set(
      familyMembers.map(m => m.email || m.phone).filter(Boolean)
    );
    
    const remainingGuests = selectedGuests.filter(guest => 
      !memberIdentifiers.has(guest.email || guest.phone || '')
    );
    
    onGuestsChange(remainingGuests);
  };

  if (familyGroups.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <Home className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">
            No family groups found. Create a family group first to manage family members.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Home className="w-5 h-5" />
          Family Members
        </CardTitle>
        <CardDescription>
          Quickly add family members to your event
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {!propFamilyGroupId && (
          <div className="space-y-2">
            <label className="text-sm font-medium">Select Family Group</label>
            <Select value={selectedFamilyGroupId} onValueChange={setSelectedFamilyGroupId}>
              <SelectTrigger>
                <SelectValue placeholder="Choose a family group" />
              </SelectTrigger>
              <SelectContent>
                {familyGroups.map((group) => (
                  <SelectItem key={group.id} value={group.id}>
                    {group.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {selectedFamilyGroupId && (
          <>
            {loading ? (
              <div className="text-center py-4">
                <p className="text-muted-foreground">Loading family members...</p>
              </div>
            ) : familyMembers.length > 0 ? (
              <>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    {familyMembers.length} family members available
                  </p>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={selectAllMembers}>
                      Select All
                    </Button>
                    <Button size="sm" variant="outline" onClick={clearAllMembers}>
                      Clear All
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {familyMembers.map((member) => {
                    const isSelected = isMemberSelected(member);
                    
                    return (
                      <div
                        key={member.id}
                        className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                          isSelected ? 'bg-primary/5 border-primary' : 'hover:bg-muted/50'
                        }`}
                        onClick={() => handleMemberToggle(member)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Checkbox checked={isSelected} />
                            <div>
                              <p className="font-medium">{member.name || 'Unnamed Member'}</p>
                              <div className="flex gap-2 text-sm text-muted-foreground">
                                {member.email && <span>{member.email}</span>}
                                {member.phone && <span>{member.phone}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-xs">
                              {member.role}
                            </Badge>
                            {isSelected && (
                              <CheckCircle className="h-4 w-4 text-primary" />
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <Users className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                <p className="text-muted-foreground">
                  No active family members found in this group.
                </p>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};